import { FC, useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Autocomplete from "@mui/material/Autocomplete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import {
	useGetRoles,
	useCreateRole,
	useUpdateRole,
	useDeleteRole,
	RoleUpdateRequest,
} from "../../hooks/useRoles";
import { useGetAllActions } from "../../hooks/useActions";
import { IRole } from "../../domain/role.interface";
import { IAction } from "../../domain/action.interface";
import { useUserStore } from "../../stores/user.store";

const RolesComponent: FC = () => {
	const { userInfo } = useUserStore();
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [editingRole, setEditingRole] = useState<IRole | null>(null);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);

	const [formData, setFormData] = useState({
		code: "",
		name: "",
		actions: [] as string[],
		ouLevel: "",
		status: "ACTIVE",
		type: "CUSTOM_ROLES",
		createdBy: userInfo?.id || "SYSTEM_USER",
	});

	const {
		data: rolesResponse,
		isLoading,
		error,
		refetch,
	} = useGetRoles(search || undefined);
	const { data: actionsResponse } = useGetAllActions();

	const createMutation = useCreateRole();
	const updateMutation = useUpdateRole();
	const deleteMutation = useDeleteRole();

	const allRoles = Array.isArray(rolesResponse?.body) ? rolesResponse.body : [];

	// Client-side pagination
	const startIndex = page * pageSize;
	const endIndex = startIndex + pageSize;
	const roles = allRoles.slice(startIndex, endIndex);
	const totalCount = allRoles.length;
	const availableActions = actionsResponse?.body?.data || [];

	const handleCreateRole = async () => {
		try {
			await createMutation.mutateAsync(formData);
			handleCloseForm();
			refetch();
		} catch (err) {
			// Error will be handled by React Query
		}
	};

	const handleUpdateRole = async () => {
		if (!editingRole) return;

		try {
			const updateData: RoleUpdateRequest = {
				...formData,
				_id: editingRole._id || editingRole.id,
				updatedBy: userInfo?.id || "SYSTEM_USER",
			};
			await updateMutation.mutateAsync(updateData);
			handleCloseForm();
			// The cache invalidation is handled by the mutation's onSuccess callback
		} catch (err) {
			// Error will be handled by React Query
		}
	};

	const handleDeleteRole = async (roleId: string) => {
		if (window.confirm("Are you sure you want to delete this role?")) {
			try {
				await deleteMutation.mutateAsync(roleId);
				refetch();
			} catch (err) {
				// Error will be handled by React Query
			}
		}
	};

	const handleEditRole = (role: IRole) => {
		setEditingRole(role);
		setFormData({
			code: role.code,
			name: role.name,
			actions: role.actionIds || role.actions || [],
			ouLevel: role.ouLevel || "",
			status: role.status || "ACTIVE",
			type: role.type || "CUSTOM_ROLES",
			createdBy: userInfo?.id || "SYSTEM_USER",
		});
		setShowCreateForm(true);
	};

	const handleCloseForm = () => {
		setShowCreateForm(false);
		setEditingRole(null);
		setFormData({
			code: "",
			name: "",
			actions: [],
			ouLevel: "",
			status: "ACTIVE",
			type: "CUSTOM_ROLES",
			createdBy: userInfo?.id || "SYSTEM_USER",
		});
	};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(event.target.value);
		setPage(0); // Reset to first page when searching
	};

	const handlePageChange = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPageSize(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleActionsChange = (
		_event: React.SyntheticEvent,
		value: IAction[],
	) => {
		const actionIds = value.map((action) => action._id);
		setFormData({ ...formData, actions: actionIds });
	};

	if (isLoading) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				height="400px"
			>
				<Typography>Loading roles...</Typography>
			</Box>
		);
	}

	if (error) {
		return (
			<Box>
				<Typography color="error">
					Error loading roles: {String(error)}
				</Typography>
			</Box>
		);
	}

	return (
		<Box>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				mb={3}
			>
				<Typography variant="h4">Roles Management</Typography>
				<Button
					variant="contained"
					color="primary"
					startIcon={<AddIcon />}
					onClick={() => setShowCreateForm(true)}
				>
					Add New Role
				</Button>
			</Box>

			{/* Search Bar */}
			<Box mb={3}>
				<TextField
					fullWidth
					placeholder="Search roles by name, description, or code..."
					value={search}
					onChange={handleSearchChange}
					InputProps={{
						startAdornment: (
							<SearchIcon sx={{ mr: 1, color: "action.active" }} />
						),
					}}
				/>
			</Box>

			{/* Create/Edit Form */}
			{showCreateForm && (
				<Card sx={{ mb: 3 }}>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							{editingRole ? "Edit Role" : "Create New Role"}
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Code"
									value={formData.code}
									onChange={(e) =>
										setFormData({ ...formData, code: e.target.value })
									}
									required
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Name"
									value={formData.name}
									onChange={(e) =>
										setFormData({ ...formData, name: e.target.value })
									}
									required
								/>
							</Grid>
							<Grid item xs={6}>
								<Autocomplete
									options={["ACTIVE", "INACTIVE"]}
									value={formData.status}
									onChange={(event, newValue) => {
										setFormData({ ...formData, status: newValue || "ACTIVE" });
									}}
									renderInput={(params) => (
										<TextField {...params} label="Status" required />
									)}
								/>
							</Grid>
							<Grid item xs={6}>
								<Autocomplete
									options={[
										"STANDARD_SYSTEM_ROLES",
										"STANDARD_ACCOUNT_ROLES",
										"CUSTOM_ROLES",
									]}
									getOptionLabel={(option) => {
										switch (option) {
											case "STANDARD_SYSTEM_ROLES":
												return "Standard System Roles";
											case "STANDARD_ACCOUNT_ROLES":
												return "Standard Account Roles";
											case "CUSTOM_ROLES":
												return "Custom Roles";
											default:
												return option;
										}
									}}
									value={formData.type}
									onChange={(event, newValue) => {
										setFormData({
											...formData,
											type: newValue || "CUSTOM_ROLES",
										});
									}}
									renderInput={(params) => (
										<TextField {...params} label="Role Type" required />
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<Autocomplete
									options={[
										{
											_id: "663afdcf54cadfe4e96f1eb3",
											level: "663afdcf54cadfe4e96f1eb3",
											name: "Root",
										},
									]}
									getOptionLabel={(option) =>
										`Level ${option.level} - ${option.name}`
									}
									onChange={(event, newValue) => {
										setFormData({ ...formData, ouLevel: newValue?._id || "" });
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Organization Level"
											placeholder="Select organization level"
											required
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<Autocomplete
									multiple
									options={availableActions}
									getOptionLabel={(option) => `${option.code} - ${option.name}`}
									value={availableActions.filter((action) =>
										(formData.actions || []).includes(action._id),
									)}
									onChange={handleActionsChange}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Actions"
											placeholder="Select actions for this role"
										/>
									)}
									renderTags={(tagValue, getTagProps) =>
										tagValue.map((option, index) => (
											<Chip
												label={option.code}
												{...getTagProps({ index })}
												size="small"
											/>
										))
									}
								/>
							</Grid>
						</Grid>
						<Box mt={2} display="flex" gap={2}>
							<Button
								variant="contained"
								color="primary"
								onClick={editingRole ? handleUpdateRole : handleCreateRole}
								disabled={createMutation.isPending || updateMutation.isPending}
							>
								{editingRole ? "Update Role" : "Create Role"}
							</Button>
							<Button variant="outlined" onClick={handleCloseForm}>
								Cancel
							</Button>
						</Box>
					</CardContent>
				</Card>
			)}

			{/* Roles Table */}
			<Paper>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Code</TableCell>
								<TableCell>Name</TableCell>
								<TableCell>Actions Count</TableCell>
								<TableCell>Actions</TableCell>
								<TableCell align="right">Management</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{roles.map((role) => (
								<TableRow key={role._id}>
									<TableCell>
										<Chip label={role.code} size="small" />
									</TableCell>
									<TableCell>
										<Typography variant="body2" fontWeight="medium">
											{role.name}
										</Typography>
									</TableCell>
									<TableCell>
										<Chip
											label={`${(role.actions || role.actionIds || []).length} actions`}
											size="small"
											color="primary"
											variant="outlined"
										/>
									</TableCell>
									<TableCell>
										<Box display="flex" flexWrap="wrap" gap={0.5}>
											{role.actions?.slice(0, 3).map((actionId) => {
												const action = availableActions.find(
													(a) => a._id === actionId,
												);
												return action ? (
													<Chip
														key={actionId}
														label={action.code}
														size="small"
														variant="outlined"
													/>
												) : null;
											})}
											{role.actions && role.actions.length > 3 && (
												<Chip
													label={`+${role.actions.length - 3} more`}
													size="small"
													variant="outlined"
													color="secondary"
												/>
											)}
										</Box>
									</TableCell>
									<TableCell align="right">
										<IconButton
											size="small"
											onClick={() => handleEditRole(role)}
											color="primary"
										>
											<EditIcon />
										</IconButton>
										<IconButton
											size="small"
											onClick={() => handleDeleteRole(role._id)}
											color="error"
											disabled={deleteMutation.isPending}
										>
											<DeleteIcon />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					rowsPerPageOptions={[5, 10, 25, 50]}
					component="div"
					count={totalCount}
					rowsPerPage={pageSize}
					page={page}
					onPageChange={handlePageChange}
					onRowsPerPageChange={handlePageSizeChange}
				/>
			</Paper>
		</Box>
	);
};

export default RolesComponent;
